import { Link } from 'react-router-dom';
import { FileText, ExternalLink, Play, ArrowRight, Cog } from 'lucide-react';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import { INSTALL_RESOURCES, WARRANTY } from '../content/products';
import usePageMeta from '../hooks/usePageMeta';

function Res({ icon, title, meta, href, internal, testid }) {
  const inner = (
    <>
      {icon}
      <span className="flex-1">
        <span className="block text-sm font-medium text-white">{title}</span>
        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-mute">{meta}</span>
      </span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-mute" aria-hidden="true" />
    </>
  );
  const cls = 'flex items-center gap-4 border border-line bg-base p-5 transition-colors hover:border-silver';
  if (internal) {
    return <Link to={href} className={cls} data-testid={testid}>{inner}</Link>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
      {inner}
    </a>
  );
}

export default function Resources() {
  usePageMeta(
    'Installation, Warranty & Support Resources | RangeSafe',
    'RangeSafe commercial resources: installation guides, compatibility information and warranty and support.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Resources"
          lines={['Resources']}
          lead="Installation, care, warranty and support."
        />
      </header>

      <div className="mx-auto max-w-shell space-y-20 px-5 pb-24 sm:px-8">
        <section id="installation" className="scroll-mt-24" data-testid="resources-installation">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">Installation &amp; care</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <Res icon={<FileText className="h-5 w-5 shrink-0 text-accentText" aria-hidden="true" />} title={INSTALL_RESOURCES.guide.title} meta="Official installation instructions (PDF)" href={INSTALL_RESOURCES.guide.href} testid="res-install-guide" />
            <Res icon={<FileText className="h-5 w-5 shrink-0 text-accentText" aria-hidden="true" />} title={INSTALL_RESOURCES.extension.title} meta="For stoves that require insert extensions (PDF)" href={INSTALL_RESOURCES.extension.href} testid="res-extension-guide" />
            {INSTALL_RESOURCES.videos.map((v) => (
              <Res key={v.href} icon={<Play className="h-5 w-5 shrink-0 text-accentText" aria-hidden="true" />} title={v.title} meta="Video demonstration (Vimeo)" href={v.href} testid={`res-video-${v.href.split('/').pop()}`} />
            ))}
            <Res icon={<Cog className="h-5 w-5 shrink-0 text-accentText" aria-hidden="true" />} title="How the Mechanism Works" meta="Push, Press and Turn™ — both families explained" href="/how-it-works" internal testid="res-mechanism" />
          </div>
        </section>

        <section data-testid="resources-warranty">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">Warranty &amp; support</h2>
          <div className="mt-8 border border-line bg-panel/60 p-8">
            <p className="text-sm leading-relaxed text-dim">{WARRANTY.term}</p>
            <p className="mt-3 text-sm leading-relaxed text-dim">{WARRANTY.exclusions}</p>
            <p className="mt-3 text-sm leading-relaxed text-dim">{WARRANTY.retail}</p>
            <p className="mt-3 font-mono text-xs text-dim">{WARRANTY.support}</p>
            <Link to="/warranty" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white border-b border-line pb-1 hover:border-accent" data-testid="res-warranty-link">
              Read the full warranty <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
